export const CREATOR_ANALYTICS_RANGES = ['7d', '30d', '90d'] as const;

export type CreatorAnalyticsRange = (typeof CREATOR_ANALYTICS_RANGES)[number];

export type UtcAnalyticsPeriod = {
  start: Date;
  end: Date;
};

export type CreatorAnalyticsWindow = {
  range: CreatorAnalyticsRange;
  days: 7 | 30 | 90;
  current: UtcAnalyticsPeriod;
  previous: UtcAnalyticsPeriod;
};

export type AnalyticsDelta = {
  absolute: number;
  percent: number | null;
};

export type AnalyticsMetric = {
  current: number;
  previous: number;
  delta: AnalyticsDelta;
};

export type CreatorAnalyticsModuleKey =
  | 'commerce'
  | 'services'
  | 'courses'
  | 'memberships'
  | 'events'
  | 'audience'
  | 'communities'
  | 'newsletters';

export type CreatorAnalyticsModuleRead = {
  key: CreatorAnalyticsModuleKey;
  status: 'available' | 'disabled' | 'unavailable';
  catalogCount: number;
  previousCatalogCount?: number;
  currentCount: number;
  previousCount: number;
  attributedGrossCents?: number;
  attributedNetCents?: number;
  drillDownRoute: string;
  message?: string;
};

export interface CreatorAnalyticsModuleReader {
  getCreatorAnalyticsRead(
    creatorId: string,
    window: CreatorAnalyticsWindow,
  ): Promise<CreatorAnalyticsModuleRead>;
}

export type CreatorAnalyticsLedgerRead = {
  currency: string | null;
  mixedCurrencies: boolean;
  currencyGroups: Array<{
    currency: string;
    currentGrossCents: number;
    previousGrossCents: number;
    currentNetCents: number;
    previousNetCents: number;
    daily: Array<{ date: string; grossCents: number; netCents: number; sales: number }>;
  }>;
  currentGrossCents: number;
  previousGrossCents: number;
  currentNetCents: number;
  previousNetCents: number;
  currentSales: number;
  previousSales: number;
  currentCustomers: number;
  previousCustomers: number;
  daily: Array<{ date: string; grossCents: number; netCents: number; sales: number }>;
};

export function isCreatorAnalyticsRange(value: string): value is CreatorAnalyticsRange {
  return CREATOR_ANALYTICS_RANGES.includes(value as CreatorAnalyticsRange);
}

export function startOfUtcDay(value: Date): Date {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
}

export function buildCreatorAnalyticsWindow(
  range: CreatorAnalyticsRange,
  now = new Date(),
): CreatorAnalyticsWindow {
  const days = Number.parseInt(range, 10) as 7 | 30 | 90;
  const end = startOfUtcDay(now);
  end.setUTCDate(end.getUTCDate() + 1);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - days);
  const previousStart = new Date(start);
  previousStart.setUTCDate(previousStart.getUTCDate() - days);
  return {
    range,
    days,
    current: { start, end },
    previous: { start: previousStart, end: start },
  };
}

export function calculateAnalyticsDelta(current: number, previous: number): AnalyticsDelta {
  return {
    absolute: current - previous,
    percent: previous === 0 ? (current === 0 ? 0 : null) : ((current - previous) / previous) * 100,
  };
}

export function utcAnalyticsDateKey(value: Date): string {
  return value.toISOString().slice(0, 10);
}

export function listUtcDateKeys(period: UtcAnalyticsPeriod): string[] {
  const keys: string[] = [];
  for (let cursor = new Date(period.start); cursor < period.end; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    keys.push(utcAnalyticsDateKey(cursor));
  }
  return keys;
}
